/**
 * SalonKrokiPage.jsx — Tek salonun kroki sayfası
 * Route: /fuar/salon/:salonId   (isteğe bağlı ?fuarId=298)
 *
 * Bu yol bilinçli olarak proje geneli /Management/... kalıbının dışındadır —
 * kroki tarafını yazan geliştiricinin istediği adres birebir budur
 * ("/fuar/salon/3 → 3 numaralı salonu açacağım"). Sayfayı /Management/Kroki
 * (KrokiPage.jsx) salon listesindeki "Krokiyi Aç" düğmeleri besler.
 *
 * Sayfanın tamamı gerçek veriyle çalışır (salon künyesi + salondaki standlar).
 * 2026-08-25: ortadaki büyük çerçeve artık rezerve DEĞİL — kroki oraya gerçekten
 * gömülü (fdsvector iframe'i, bkz. Components/Kroki/KrokiVectorEmbed.jsx).
 * Eski KrokiMountFrame placeholder'ı yalnızca fuar alanı id'si okunamadığında
 * (gömme için gereken fairgroundId yoksa) görünür.
 *
 * :salonId salon TABLOSUNUN id'sidir (ör. fuar 295'te 1..12, 49, 92), stand
 * adındaki "1.1A20" ön eki değil — ikisi her zaman örtüşmez, bkz.
 * docs/guides/FE-StandTalepController-guide.md.
 */
import React, { useContext, useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Container, Card, CardBody, CardHeader, Row, Col, Button, Badge,
  Spinner, Alert, Table,
} from "reactstrap";
import {
  Map as MapIcon, ArrowLeft, RefreshCw, Store, Building2, AlertTriangle,
} from "lucide-react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import MainContext from "../../../Components/Contexts/MainContext";
import KrokiMountFrame from "../../../Components/Kroki/KrokiMountFrame";
import KrokiVectorEmbed from "../../../Components/Kroki/KrokiVectorEmbed";
import { getSalonDetay } from "../../../services/krokiService";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

const SalonKrokiPage = () => {
  const { t: tx } = useTranslation("yonetim");
  const { salonId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedFuarId } = useContext(MainContext);

  // Fuar bağlamı: önce URL'deki ?fuarId=, yoksa uygulamada seçili fuar.
  // (Salon kaydı fuardan bağımsız ama standlar fuara bağlı sorgulanıyor.)
  const fuarId = searchParams.get("fuarId") || selectedFuarId || null;

  const [detay, setDetay] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => { document.title = i18n.t("yonetim:salonKroki.salonKrokisiFds"); }, []);

  // KURAL: fetch Effect içinde tanımlanır, dışarı paylaşılmaz (react-hooks/set-state-in-effect).
  useEffect(() => {
    if (!salonId) return;
    let ignore = false;

    async function run() {
      setLoading(true);
      setError(null);
      try {
        const data = await getSalonDetay(salonId, fuarId);
        if (!ignore) setDetay(data);
      } catch (e) {
        if (!ignore) setError(i18n.t("yonetim:salonKroki.salonBilgisiAlinamadi") + e.message + ")");
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    run();
    return () => { ignore = true; };
  }, [salonId, fuarId, reloadToken]);

  const salonBaslik = detay?.salonName ? tx("salonKroki.salon", { salonName: detay.salonName }) : tx("salonKroki.salon2", { salonId });

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={salonBaslik} pageTitle={tx("salonKroki.kroki")} />

        <Button color="light" className="mb-3" onClick={() => navigate("/Management/Kroki")}>
          <ArrowLeft size={16} className="me-1" />
          {tx("salonKroki.salonListesineDon")}
        </Button>

        {error && <Alert color="danger">{error}</Alert>}

        {!fuarId && (
          <Alert color="warning" className="d-flex align-items-start gap-2">
            <AlertTriangle size={18} className="flex-shrink-0 mt-1" />
            <div>
              {tx("salonKroki.fuarBaglamiYokSalonKunyesi")}
              <code className="mx-1">{tx("salonKroki.fuarId298")}</code>{" "}{tx("salonKroki.ekleyinYaDaUsttenBir")}
            </div>
          </Alert>
        )}

        {/* Salon künyesi */}
        <Card className="mb-4">
          <CardBody>
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div
                className="rounded-3 text-center text-white px-3 py-2 shadow-sm bg-primary"
                style={{ minWidth: 72 }}
              >
                <div className="fw-bold fs-4 lh-1">{detay?.salonName ?? "—"}</div>
                <div className="small lh-1 mt-1">#{salonId}</div>
              </div>
              <div className="flex-grow-1">
                <h4 className="text-dark mb-1">{salonBaslik}</h4>
                <div className="text-muted small">
                  {detay?.fuarName || (fuarId ? tx("salonKroki.fuar", { fuarId }) : tx("salonKroki.fuarSecilmedi"))}
                  {detay?.fuarAlaniName ? ` · ${detay.fuarAlaniName}` : ""}
                </div>
                <div className="mt-2 d-flex gap-2 flex-wrap">
                  <Badge color="secondary" pill>
                    <Store size={12} className="me-1" />
                    {tx("salonKroki.stand", { v: detay?.standCount ?? 0 })}
                  </Badge>
                  {detay?.isUnlisted && (
                    <Badge color="warning" pill title={tx("salonKroki.buSalonFuarinSalonListesinde")}>
                      {tx("salonKroki.fuarSalonListesindeYok")}
                    </Badge>
                  )}
                </div>
              </div>
              <Button color="light" onClick={() => setReloadToken((t) => t + 1)} disabled={loading}>
                <RefreshCw size={16} className="me-1" />
                {tx("common:yenile")}
              </Button>
            </div>
          </CardBody>
        </Card>

        {/* ⬇ SAYFANIN ASIL AMACI — gömülü kroki */}
        <Card className="mb-4">
          <CardHeader className="bg-white border-bottom">
            <h5 className="mb-0">
              <MapIcon size={18} className="me-2 text-primary" />
              {tx("salonKroki.krokisi", { salonBaslik })}
            </h5>
          </CardHeader>
          <CardBody>
            {/* Gerçek kroki: fdsvector uygulaması iframe olarak gömülü
                (Components/Kroki/KrokiVectorEmbed.jsx). Fuar alanı id'si
                okunamazsa eski rezerve çerçeve placeholder olarak kalır. */}
            {detay?.fuarAlaniId ? (
              <KrokiVectorEmbed
                fairgroundId={detay.fuarAlaniId}
                hallId={salonId}
                hallName={detay?.salonName ?? undefined}
                height={640}
              />
            ) : (
              <KrokiMountFrame
                mount="salon-kroki"
                mountId="kroki-salon-gorunum"
                baslik={loading ? tx("salonKroki.salonBilgisiYukleniyor") : tx("salonKroki.krokiIcinFuarAlaniBilgisi")}
                aciklama={tx("salonKroki.gomuluKrokiSalonuFuarAlani")}
                fuarId={fuarId ? Number(fuarId) : null}
                salonId={Number(salonId)}
                minHeight={640}
                meta={[
                  { label: tx("salonKroki.salonId"), value: salonId },
                  { label: tx("salonKroki.salonAdi"), value: detay?.salonName ?? "—" },
                  { label: tx("salonKroki.fuarId"), value: fuarId ?? "—" },
                  { label: tx("salonKroki.standSayisi"), value: detay?.standCount ?? 0 },
                ]}
              />
            )}
          </CardBody>
        </Card>

        {/* Salondaki standlar — gerçek veri (GET /standTalep/list?fuarId=&salonId=) */}
        <Card>
          <CardHeader className="bg-white border-bottom">
            <h5 className="mb-0">
              <Building2 size={18} className="me-2 text-primary" />
              {tx("salonKroki.salondakiStandlar")}
              {detay?.standCount ? (
                <span className="text-muted fw-normal ms-2" style={{ fontSize: 14 }}>
                  ({detay.standCount})
                </span>
              ) : null}
            </h5>
          </CardHeader>
          <CardBody>
            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <div className="text-muted small mt-2">{tx("common:yukleniyor")}</div>
              </div>
            ) : !detay?.standlar?.length ? (
              <div className="text-center text-muted py-5">
                {tx("salonKroki.buSalonda")}{fuarId ? tx("salonKroki.fuar2", { fuarId }) : ""}{" "}{tx("salonKroki.standKaydiBulunamadi")}
              </div>
            ) : (
              <div className="table-responsive">
                <Table className="align-middle table-nowrap mb-0" hover>
                  <thead className="table-light">
                    <tr>
                      <th>{tx("salonKroki.stand2")}</th>
                      <th>{tx("salonKroki.katilimciFirma")}</th>
                      <th>{tx("salonKroki.dekorator")}</th>
                      <th className="text-center">{tx("salonKroki.durum")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detay.standlar.map((s) => (
                      <tr key={s.standId}>
                        <td>
                          <span className="fw-semibold">{s.standName || "—"}</span>
                          <span className="text-muted ms-2" style={{ fontSize: 12 }}>#{s.standId}</span>
                        </td>
                        <td>{s.katilimciFirmaName || "—"}</td>
                        <td>{s.dekoratorFirmaName || "—"}</td>
                        <td className="text-center">
                          {s.standIsProblem && <Badge color="danger" className="me-1">{tx("salonKroki.problemli")}</Badge>}
                          {s.standIsForbidden && <Badge color="dark" className="me-1">{tx("salonKroki.yasakli")}</Badge>}
                          {!s.standIsProblem && !s.standIsForbidden && (
                            <Badge color="success" className="me-1">{tx("salonKroki.normal")}</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default SalonKrokiPage;
